import { IWorkOrderResourceAssignment } from "./ConfigWorkOrderTemplates.types";

export const matchesAssignmentUser = (
  resourceId: string,
  user: { resourceId: string }
) => user.resourceId === resourceId;

export const sortAssignmentsByDisplayOrder = <
  T extends { displayOrder?: number; resourceId: string }
>(
  assignments: T[]
) =>
  [...assignments].sort((left, right) => {
    const leftDisplayOrder = left.displayOrder ?? Number.MAX_SAFE_INTEGER;
    const rightDisplayOrder = right.displayOrder ?? Number.MAX_SAFE_INTEGER;

    if (leftDisplayOrder === rightDisplayOrder) {
      return left.resourceId.localeCompare(right.resourceId);
    }

    return leftDisplayOrder - rightDisplayOrder;
  });

export const withDisplayOrder = <T extends IWorkOrderResourceAssignment>(
  assignments: T[]
) =>
  assignments.map((assignment, index) => ({
    ...assignment,
    displayOrder: index + 1,
  }));

export const normalizeAssignments = (
  assignments: IWorkOrderResourceAssignment[]
) => withDisplayOrder(sortAssignmentsByDisplayOrder(assignments));

export const parseWindowMinutes = (
  value: string | null | unknown
): number | null => {
  if (!value || typeof value !== "string") return null;
  const match = value.match(/(\d{1,2}):(\d{2})/);
  if (!match) return null;
  return parseInt(match[1], 10) * 60 + parseInt(match[2], 10);
};

export const getOverlappingWindowIndices = (
  windows: Array<{
    start: string | null | unknown;
    end: string | null | unknown;
  }>
): Set<number> => {
  const overlapping = new Set<number>();
  const parsed = windows.map((w) => ({
    start: parseWindowMinutes(w.start),
    end: parseWindowMinutes(w.end),
  }));
  parsed.forEach((a, i) => {
    if (a.start === null || a.end === null) return;
    const aStart = a.start;
    const aEnd = a.end;
    parsed.forEach((b, j) => {
      if (j <= i || b.start === null || b.end === null) return;
      if (aStart < b.end && b.start < aEnd) {
        overlapping.add(i);
        overlapping.add(j);
      }
    });
  });
  return overlapping;
};

export const haveAssignmentsChanged = (
  currentAssignments: IWorkOrderResourceAssignment[],
  nextAssignments: IWorkOrderResourceAssignment[]
) =>
  currentAssignments.length !== nextAssignments.length ||
  currentAssignments.some((assignment, index) => {
    const nextAssignment = nextAssignments[index];

    return (
      assignment.resourceId !== nextAssignment?.resourceId ||
      assignment.autoAssignmentRatio !== nextAssignment?.autoAssignmentRatio ||
      assignment.performanceMultiplierPercentage !==
        nextAssignment?.performanceMultiplierPercentage ||
      assignment.displayOrder !== nextAssignment?.displayOrder
    );
  });
